import { defineStore } from "pinia";
import type { QuestionSchema } from "@/features/blueprint/schema/questions/QuestionSchema";
import { Answers } from "@/features/blueprint/schema/answers/Answers";
import { AnswerValue } from "@/features/blueprint/schema/answers/AnswerValue";
import { isValidAnswer } from "@/features/blueprint/schema/answers/answerValidation";

export const useFormStore = defineStore("form", {
    state: () => ({
        sections: [] as QuestionSchema[],
        currentSectionIndex: 0,
        answers: {} as Answers,
    }),

    actions: {
        loadFromLocalStorage() {
            const raw = localStorage.getItem("site-builder-answers");
            if (!raw) return;

            try {
                const parsed = JSON.parse(raw) as Record<string, unknown>;

                const validated: Answers = {};
                for (const [key, value] of Object.entries(parsed)) {
                    if (isValidAnswer(value)) {
                        validated[key] = value as AnswerValue;
                    }
                }

                this.answers = validated;
                console.info("[FormStore] Loaded from localStorage:", this.answers);
            } catch (err) {
                console.warn("[FormStore] Invalid localStorage data. Resetting.", err);
                this.answers = {};
            }
        },

        saveToLocalStorage() {
            localStorage.setItem("site-builder-answers", JSON.stringify(this.answers));
            console.info("[FormStore] Saved to localStorage:", this.answers);
        },

        setSections(sections: QuestionSchema[]) {
            this.sections = sections;
            console.info("[FormStore] Sections set:", sections);

            for (const section of sections) {
                for (const question of section.questions) {
                    const id = question.id;

                    if (!(id in this.answers)) {
                        if (question.type === "multiselect" || question.type === "colorpicker") {
                            this.answers[id] = [];
                        } else if (question.type === "checkbox") {
                            this.answers[id] = false;
                        } else {
                            this.answers[id] = "";
                        }
                    }
                }
            }

            console.info("[FormStore] Initialized answers with defaults:", this.answers);
        },

        completeCurrentSection() {
            if (this.currentSectionIndex < this.sections.length) {
                this.currentSectionIndex++;
                console.debug("[FormStore] Moved to next section:", this.currentSectionIndex);
            }
        },

        prevSection() {
            if (this.currentSectionIndex > 0) {
                this.currentSectionIndex--;
                console.debug("[FormStore] Moved to previous section:", this.currentSectionIndex);
            }
        },

        resetCompletion() {
            this.currentSectionIndex = this.sections.length - 1;
            console.debug("[FormStore] Completion reset to last section:", this.currentSectionIndex);
        },
    },
});
